import Vue from 'vue'
import moment from 'moment'
import momentTimeZone from 'moment-timezone'
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import isString from "lodash/isString";
import {
  getTimeFraction,
  revertTimeFraction,
  determineIfStoreIsClosed,
  getStoreOpenAndClose,
  nowInStoreTz,
  getStoreOpenAndCloseNextBusinessDay,
  getNextBusinessDay,
  isWeChat,
  isBeforeOpen,
  isAfterClose,
  matchTranslation,
  convertTimeFraction,
  ensureDateObject,
  compareDates,
  formatDate,
  formatTime,
  getDisplayLocale,
  formateDateTime,
  scanSchedule,
  isNullOrEmpty,
  revertLangCode,
  convertLangCode,
  slugifyMenuItemName
} from "@/services/util.service"

import { isInSchedule } from "@/services/schedule.service";

const normalizeStoreSlug = (store) => {
  // Always prefer the English name for the slug so the URL is language-independent,
  // regardless of the store's default language or what the API slug field contains.
  const translations = get(store, 'nameTranslation.translations', [])
  const safeTranslations = Array.isArray(translations) ? translations : []
  const enTranslation = safeTranslations.find(t => t.lang === 'en' && t.value)
  if (enTranslation) {
    return { ...store, slug: slugifyMenuItemName(enTranslation.value) }
  }
  return store
}

const determineInitialLang = (getters, dispatch) => {
  let stateLang = localStorage.getItem('lang')
  let userHasManuallySelected = localStorage.getItem('langManuallySelected') === 'true'
  if (userHasManuallySelected && stateLang) {
    const storeLangs = getters.getStoreLanguages
    const lookupCode = revertLangCode(stateLang)
    const storeHasCurrentLang = storeLangs.find(s => s.code === lookupCode)
    if (storeHasCurrentLang) {
      return
    }
  }
  const store = getters.getStore
  let displayLanguages = (store && store.settings && store.settings.orderSettings && store.settings.orderSettings.displayLanguages)
  if (!displayLanguages || !Array.isArray(displayLanguages) || displayLanguages.length === 0) {
    displayLanguages = (store && store.settings && store.settings.owaSettings && store.settings.owaSettings.displayLanguages)
  }

  let priorityLang = null
  if (displayLanguages && Array.isArray(displayLanguages) && displayLanguages.length > 0) {
    priorityLang = displayLanguages[0]
  }

  let currentLang = stateLang
  if (!userHasManuallySelected) {
    if (priorityLang) {
      currentLang = priorityLang
    }
    else if (!currentLang) {
      // No orderSettings/owaSettings.displayLanguages configured — fall back to the
      // store's own languages[].selected flag (e.g. { code: 'fr', selected: true })
      // before guessing from the browser locale.
      const storeLangs = getters.getStoreLanguages
      const { code: defaultLangCode } = storeLangs.find(l => l.selected === true) || {}
      if (defaultLangCode) {
        currentLang = defaultLangCode
      }
      else if (navigator.language.includes('en'))
        currentLang = 'en'
      else if (navigator.language.includes('fr'))
        currentLang = 'fr'
      else if (navigator.language.includes('es'))
        currentLang = 'es'
      else if (navigator.language.includes('ko'))
        currentLang = 'ko'
      else
        currentLang = 'zhHans'
    }
  }

  if (!currentLang) {
    const storeLangs = getters.getStoreLanguages
    const { code: defaultLangCode } = storeLangs.find(l => l.selected === true) || {}
    const { code: firstLangCode } = get(storeLangs, '[0]') || {}

    if (defaultLangCode) {
      currentLang = defaultLangCode
    } else if (firstLangCode) {
      currentLang = firstLangCode
    } else {
      currentLang = 'en'
    }
  }

  const storeLangs = getters.getStoreLanguages
  const lookupCode = revertLangCode(currentLang)
  const storeHasCurrentLang = storeLangs.find(s => s.code === lookupCode)

  let langCode = null
  if (storeHasCurrentLang) {
    langCode = convertLangCode(currentLang)
  } else {
    const { code: firstLangCode } = get(storeLangs, '[0]') || {}
    langCode = firstLangCode || 'en'
  }
  if (langCode && langCode !== stateLang) {
    dispatch('doChangeLang', langCode)
  }
}

const storeBlockedReason = (getters) => {
  const _t = Vue.prototype.$vuetify.t
  
  // If the store hours are valid but no time slots are found, there is most likely a schedule being applied
  if (getters.hasOrder() && getters.hoursAreAllowed && !getters.getStoreAvailableOrderDays.length)
    return _t('$vuetify.cart.cartUnavailable')

  if (!getters.hoursAreAllowed)
    return `${getters.getStore.name || 'This store'} ${_t('$vuetify.cart.currentlyClosed')}`
}

export default {
  state: {
    store: {},
    isStoreLoading: false,
    group: {},
    groupStores: [],
    groupStoresSort: 'alpha', // 'alpha' or 'location', currently supported
    isGroupStoresLoading: false,
    dineInOnly: false,
    customHeader: false,
  },

  getters: {
    isMobile(state) {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isSmallScreen = window.innerWidth <= 600;
      return /mobile|android|iphone|ipad|ipod/.test(userAgent) || isSmallScreen;
    },
    shouldUseCustomHeader(state) {
      return state.customHeader
    },
    doesStoreSupportFeature: (state, getters) => (feature) => {
      return !!get(getters.getStore, `features[${feature}]`, false)
    },
    doesStoreHaveSetting: (state, getters) => (feature, defaultVal=false, store=getters.getStore) => {
      return !!get(store, `settings.orderSettings[${feature}]`, defaultVal)
    },


    tillNextOpen: (state, getters) => (orderType = getters.getOrderType) => {
      const now = new Date()
      const { open } = getStoreOpenAndClose(getters.getStore, now)
      const currentTimeFraction = getTimeFraction(now.getHours(), now.getMinutes())

      // Dtermine if the store opens later today
      if (currentTimeFraction < open) {
        return open - currentTimeFraction
      } else {
        // If not, get the first available open and include the 24 hour difference per day
        const day = new Date()
        const currentTime = now.getTime()
        let firstOpen = null
        let dayDifference = 0

        // Loop until an open can be found capping at the next week (avoiding infinite loops)
        while ((firstOpen == null || isNaN(firstOpen)) && dayDifference <= 7) {
          day.setDate(day.getDate() + 1)
          firstOpen = getters.getEarliestFraction(day, orderType)
          dayDifference = (day.getTime() - currentTime) / (86400000)
        }

        // number of hours until the open + the earliest the store allows orders day - progress into current day
        return (dayDifference * 24) + firstOpen - currentTimeFraction
      }
    },
    hourLimitIsPastOpen(state, getters) {
      return (getters.tillNextOpen() - getters.getHoursLimit) <= 0
    },
    allowsOrderOffHours(state, getters) {
      if (getters.isPickUp)
        return getters.doesStoreHaveSetting('allowOffHourPickup', false)
      if (getters.isDelivery)
        return getters.doesStoreHaveSetting('allowOffHourDelivery', false)

      // Can never dine in during off hours
      return false
    },
    limitAllowsOrder(state, getters) {
      return getters.getHoursLimit >= 0 && getters.hourLimitIsPastOpen
    },
    canOrderOffHours(state, getters) {
      // limitAllowsOrder answers a DIFFERENT question (how far in advance can this be
      // scheduled, via scheduledOrderTimeLimitInHours/scheduledDeliveryTimeLimitInHours)
      // than allowsOrderOffHours (can the store take orders at all while closed, via
      // allowOffHourPickup/Delivery). When the hours-limit setting is left unset (0),
      // getHoursLimit's 0 reads as "no scheduling window at all" rather than "no limit
      // configured" -- which blocked off-hour ordering entirely even with
      // allowOffHourPickup/Delivery explicitly on, unless a merchant ALSO separately
      // configured an hours limit. The merchant turning on off-hour ordering is enough
      // on its own; don't require a second, independent setting to also be configured.
      return getters.allowsOrderOffHours && (getters.getHoursLimit > 0 ? getters.limitAllowsOrder : true)
    },
    currentOrderTypeAllowed(state, getters) {
      const storeFeatures = get(getters.getStore, 'features', {})
      const owaSettings = get(getters.getStore, 'settings.owaSettings', {})
      const featureKey = get(getters.getOrderTypeObject, 'key', null)
      const settingsKey = getters.getOrderType.toLowerCase()+'Enabled'

      return get(storeFeatures, featureKey, false) && get(owaSettings, settingsKey, false)
    },
    orderTypeIsDisabledUntil(state, getters) {
      if (getters.isPickUp)
        return get(getters.getStore, 'settings.orderSettings.onlinePickupDisabledUntil', null)
      if (getters.isDelivery)
        return get(getters.getStore, 'settings.orderSettings.onlineDeliveryDisabledUntil', null)
      return null
    },
    orderTypeIsDisabled(state, getters) {
      const disabledUntil = getters.orderTypeIsDisabledUntil

      if (disabledUntil != null) {
        const date = ensureDateObject(disabledUntil)

        // Ensure that the current datetime is after the disabled until
        return compareDates(date, new Date()) < 0
      }

      return false
    },
    daysAreAvailable(state, getters) {
      const daysAvailable = getters.getStoreAvailableOrderDays
      return !!(daysAvailable && daysAvailable.length)
    },
    hoursAreAllowed(state, getters) {
      return getters.storeIsOpen || getters.canOrderOffHours
    },
    storeIsAvailable(state, getters) {
      return getters.hoursAreAllowed && getters.daysAreAvailable
    },
    // isOffHours(state, getters) {
    //   const store = getters.getStore
    //   return isBeforeOpen(store) || isAfterClose(store)
    // },

    getStoreNotice(state, getters) {
      const customNotice = matchTranslation(get(getters, 'getStore.settings.owaSettings.noticeMessage.translations', []))
      if (customNotice && customNotice.trim() !== '') {
        return customNotice
      }
      return getters.getAutomaticPricingBanner || ''
    },
    
    getAutomaticPricingBanner(state, getters, rootState, rootGetters) {
      const menu = rootGetters.getMenu
      const channelPriceComparison = menu && menu.channelPriceComparison

      if (!channelPriceComparison || Object.keys(channelPriceComparison).length === 0) {
        return null
      }

      const platformDisplayNames = {
        'UBEREATS': 'Uber Eats',
        'DOORDASH': 'DoorDash',
        'SKIPTHEDISHES': 'Skip The Dishes',
        'GRUBHUB': 'Grubhub',
        'POSTMATES': 'Postmates',
        'FOODPANDA': 'foodpanda',
        'DELIVEROO': 'Deliveroo',
        'JUSTEAT': 'Just Eat',
        'GOOGLE': 'Google',
        'FANTUAN': 'Fantuan'
      }
      const entries = Object.entries(channelPriceComparison)
      if (entries.length === 0) {
        return null
      }

      const currentLang = Vue.prototype.$vuetify.lang.current;
      const group = state.group || {};
      const store = state.store || {};
      let currentLangTranslation = '';

      if (
        group.groupNameTranslation &&
        Array.isArray(group.groupNameTranslation.translations) &&
        group.groupNameTranslation.translations.length > 0
      ) {
        const found = group.groupNameTranslation.translations.find(
          translation => translation.lang === currentLang && translation.value
        );
        currentLangTranslation = found ? found.value : (group.groupNameTranslation.translations[0].value || '');
      } else if (
        store.nameTranslation &&
        Array.isArray(store.nameTranslation.translations) &&
        store.nameTranslation.translations.length > 0
      ) {
        const found = store.nameTranslation.translations.find(
          translation => translation.lang === currentLang && translation.value
        );
        currentLangTranslation = found ? found.value : (store.nameTranslation.translations[0].value || '');
      } else {
        currentLangTranslation = store.name || '';
      }

      const [bestPlatform, bpsValue] = entries[0]
      const percentage = bpsValue / 100
      if (percentage >= 1) {
        const platformName = platformDisplayNames[bestPlatform] || bestPlatform
        const savingsPercent = Math.round(percentage)

        return Vue.prototype.$vuetify.t('$vuetify.menu.pricingBanner', savingsPercent, platformName, currentLangTranslation)
      }

      return null
    },
    getGroupNotice(state, getters) {
      return matchTranslation(get(getters, 'getGroup.settings.owaSettings.noticeMessage.translations', []))
    },
    getStoreEstimatedTime(state) {
      return get(state, 'store.settings.orderSettings.estimatedPickupTimeInMinutes', 0)
    },
    doesGroupHaveMultipleStores(state) {
      return get(state, 'groupStores', []).length > 1
    },
    getIsGroupStoresLoading(state) {
      return state.isGroupStoresLoading
    },
    getGroupStoresSort(state) {
      return state.groupStoresSort
    },
    hasLang: (state, getters) => (code) => {
      return find(getters.getStoreLanguages, { code })
    },
    langIsSelected: (state, getters) => (lang) => {
      return revertLangCode(Vue.prototype.$vuetify.lang.current) === lang.code
    },
    getStoreLanguages(state, getters) {
      let _displayLanguages = get(getters.getStore, 'settings.orderSettings.displayLanguages')
      if (!_displayLanguages || !Array.isArray(_displayLanguages) || _displayLanguages.length === 0) {
        _displayLanguages = get(getters.getStore, 'settings.owaSettings.displayLanguages') || []
      }

      // On OWA, this list takes priority but keep using store.languages if empty
      if (!isNullOrEmpty(_displayLanguages))
        return _displayLanguages.map(_item => {
          return {
            code: _item,
            selected: _item == _displayLanguages[0] // first in the list is the default
          }
        })

      return get(getters.getStore, 'languages') || []
    },
    getStateOrderTypes(state, getters) {
      const defaultOrderTypes = ['PICKUP', 'DELIVERY', 'DINE_IN']

      // Some stores restrict dine in unless the user accessses the app via qrcodes in the store
      const allowDinein = !get(getters.getStore, 'settings.orderSettings.disableOnlineDineIn', false) || getters.getTableId != -1 || getters.getEnteredViaQr

      return getters.getStoreOrderTypes.filter(orderType => {
        return defaultOrderTypes.includes(orderType.value)            // We only support specific options
          && (orderType.value == 'DINE_IN' || !getters.isInKioskMode) // Kiosk Mode only supports dine in options
          && (orderType.value != 'DINE_IN' || allowDinein)            // When disableOnlineDineIn is on, dine in is only allowed if they arrived via qr code located in the store
      })
    },
    hasMultipleMenus(state, getters) {
      return (getters.getAvailableMenus || []).length > 1
    },
    getUniqueStoreId(state) {
      const { id = '', storeId = '', groupId = '' } = state.store

      // If the ID doesn't exist, we create our own ID using the groupId, and storeId
      return id || `${groupId}_${storeId}`
    },
    getStore(state) {
      return state.store
    },
    getCurrencyCode(state) {
      return (state.store && state.store.currency) || 'USD';
    },
    getDineInOnly(state) {
      return state.dineInOnly
    },
    isStoreLoading(state) {
      return state.isStoreLoading
    },

    testLeeway: (state, getters) => (day, orderType = getters.getOrderType, store = getters.getStore) => {
      // Dine in has no leeway
      if (orderType == 'DINE_IN')
        return true

      // Default to "now" in the STORE's timezone, not the browser's — day
      // can't default off `store` directly since it's declared later in the
      // signature, so resolve it here once store is available.
      if (day === undefined) day = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))

      const leewayMinutes = orderType === 'DELIVERY' ? 45 : 30
      const { open } = getStoreOpenAndClose(store, day)

      return getTimeFraction(day.getHours(), day.getMinutes()) >= getTimeFraction(open, leewayMinutes)
    },
    testToday: (state, getters) => (day, store = getters.getStore) => {
      if (day === undefined) day = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))

      let {open, close} = getStoreOpenAndClose(store, day)
      // The backend shifts the open time forward by advanceBookingAfterStoreOpenInMinutes.
      // Reverse that offset so the open/closed display reflects the real store opening time.
      const advanceMinutes = get(store, 'settings.orderSettings.advanceBookingAfterStoreOpenInMinutes', 0)
      if (advanceMinutes > 0 && open !== undefined) {
        const { hours: openHours, minutes: openMinutes } = convertTimeFraction(open)
        const totalMinutes = Math.max(0, openHours * 60 + openMinutes - advanceMinutes)
        open = getTimeFraction(Math.floor(totalMinutes / 60), totalMinutes % 60)
      }

      return !determineIfStoreIsClosed(open, close, day)
    },

    testYesterday: (state, getters) => (day, store = getters.getStore) => {
      if (day === undefined) day = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))

      // Since hours can go past midnight, we need to compare the current time to today and yesterdays
      // i.e. if it is 1am friday and the store closes at 3am on thursday, it is still open by 'yesterdays' hours
      // Get yesterdays hours and test if the store is open by those hours
      let yesterday = new Date(day.getTime())
      yesterday.setDate(yesterday.getDate() - 1)

      const {open, close} = getStoreOpenAndClose(store, yesterday)

      const yesterdaySpillsOver = close && close < open

      // If close is smaller than open, it is spilling into the next day. If not it has normal hours and we don't check yesterdays range
      return yesterdaySpillsOver && !determineIfStoreIsClosed(0, close, day)
    },

    testDateAgainstHours: (state, getters) => (day, orderType = getters.getOrderType, store = getters.getStore) => {
      if (day === undefined) day = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))
      // Testing hours takes into account leeway in order types
      return (getters.testToday(day, store) && getters.testLeeway(day, orderType, store)) || getters.testYesterday(day, store)
    },
    isStoreClosed(state, getters) {
      // Open is universal and does not take into account method specific leeway ranges
      return !getters.storeIsOpen
    },
    isHoliday(state, getters) {
      const store = getters.getStore;
      if (!store.catalogHolidayHours || store.catalogHolidayHours.length === 0) return false;
      
      const storeTz = store.timeZone || store.timezone || 'UTC';
      const now = momentTimeZone().tz(storeTz);
      const today = now.format('YYYY-MM-DD');
      const currentHour = now.hour() + now.minute() / 60;

      // Get all holiday entries for today
      const holidaysToday = store.catalogHolidayHours.filter(h => h.date === today);
      if (!holidaysToday.length) return false;

      // If any holiday entry for today has no timeRange or empty timeRange, treat as closed
      const hasFullDayHoliday = holidaysToday.some(h => !h.timeRange || !Array.isArray(h.timeRange) || h.timeRange.length === 0);
      if (hasFullDayHoliday) return true;

      // If all timeRanges for all holidays do not include currentHour, treat as closed
      const isInAnyHolidayRange = holidaysToday.some(h =>
        h.timeRange && Array.isArray(h.timeRange) && h.timeRange.some(range => currentHour >= range.start && currentHour < range.end)
      );

      return !isInAnyHolidayRange;
    },
    storeIsOpen(state, getters) {
      if (getters.isHoliday) {
        return false;
      }
      
      // If the store is closed, certain menus still support guests to continue ordering
      if (!(getters.testToday() || getters.testYesterday()))
        return !!getters.getMenu && getters.getMenu.doNotRestrictScheduleToStoreHours && getters.isMenuAvailable()
      return true
    },
    testStore: (state, getters) => (store = getters.getStore) => {
      const now = nowInStoreTz(get(store, 'timeZone', null) || get(store, 'timezone', null))
      return getters.testToday(now, store) || getters.testYesterday(now, store)
    },
    getPaymentSupport(state) {
      const supported = _.get(state.store, 'settings.orderSettings.paymentSettings', {})
      return {
        DELIVERY: supported[ 'DELIVERY' ],
        DINE_IN: supported[ 'DINE_IN' ],
        PICKUP: supported[ 'PICKUP' ],
        ALL: supported[ 'ALL' ]
      }
    },
    getEarliestFraction: (state, getters) => (day, orderType = getters.getOrderType) => {
      let earliestOrder, earliestHour, earliestMinutes;
      const store = getters.getStore
      let { open, close } = getStoreOpenAndClose(store, day)

      // Allow for non state dependent usage (i.e tabs select)
      let leewayMinutes = orderType === 'DELIVERY' ? 45 : 30

      if (getters.isStoreClosed) {
        earliestOrder = getNextBusinessDay(store, day)
        const { hours, minutes } = convertTimeFraction(open)
        earliestHour = hours
        earliestMinutes = minutes + leewayMinutes
      } else {
        if (day && (day == 'today' || day.toDateString() === (new Date()).toDateString())) {
          earliestOrder = new Date()
          earliestHour = earliestOrder.getHours()
          earliestMinutes = earliestOrder.getMinutes()
        } else {
          earliestOrder = getNextBusinessDay(store, day)
          const { hours, minutes } = convertTimeFraction(open)
          earliestHour = hours
          earliestMinutes = minutes + leewayMinutes
        }

      }

      return getTimeFraction(earliestHour, earliestMinutes)
    },

    getStoreAvailableOrderTimes: (state, getters) => (day = new Date(), timeSlots = []) => {
      const formatDateTime = (hours=day.getHours(), minutes=day.getMinutes()) => {
        // Clone the object to keep it's scope controlled
        const dt = new Date(day.getTime())
        dt.setHours(hours)
        dt.setMinutes(minutes)
        dt.setSeconds(0)
        dt.setMilliseconds(0)

        return moment(dt).format('LT')
      }

      const buildInterval = (i) => {
        // time intervals are "hardcoded" to 15 minutes aka 25% of an hour
        const interval = 0.25 * i

        const hours = Math.floor(interval)
        const minutesDecimal = interval - Math.floor(interval)
        const minutes = 60 * minutesDecimal

        // Clone the object to keep it's scope controlled
        const dateInterval = new Date(day.getTime())
        dateInterval.setHours(hours)
        dateInterval.setMinutes(minutes)
        dateInterval.setSeconds(0)
        dateInterval.setMilliseconds(0)

        const formatted = moment(dateInterval).format('LT')

        return { dateInterval, formatted }
      }

      // The backend's advance-booking cutoff (timeSlot.start, when it's the binding
      // constraint) is "now + advanceBookingMinInMinutes(Pickup)" computed at fetch time --
      // CPAPI.checkOrderTime re-derives that same "now + buffer" independently at submit
      // time with zero tolerance, using whatever "now" is at that later instant. Any delay
      // between fetching this grid and actually submitting (click time, network latency)
      // can push the real cutoff past a slot that was genuinely valid when offered, causing
      // ORD_ERR_SCHEDULED_TIME_TOO_CLOSE on a pick the guest made in good faith.
      //
      // We can't tell purely from timeSlot.start whether a given boundary is the
      // advance-booking cutoff or the store's opening hour (both flow through the same
      // Math.max(minHour, storeOpenHour) on the backend) -- but we don't have to guess: the
      // buffer minutes are on getStore.settings.orderSettings, so we compute our own
      // "now + buffer" the same way the backend does, round it up to the next 15-minute
      // grid step (bigger than any realistic fetch-to-submit delay), and hold back only
      // slots that fall inside that margin -- never touching a boundary caused by store
      // hours, since that's not derived from "now" at all.
      const orderSettings = get(getters.getStore, 'settings.orderSettings', {})
      const advanceBookingMinInMinutes = getters.isDelivery
        ? get(orderSettings, 'advanceBookingMinInMinutes', 0)
        : get(orderSettings, 'advanceBookingMinInMinutesPickup', 0)
      const storeTz = get(getters.getStore, 'timeZone', null) || get(getters.getStore, 'timezone', null)
      const now = nowInStoreTz(storeTz)
      const isToday = ensureDateObject(day).toDateString() === now.toDateString()
      let safeCutoffFraction = null
      if (isToday && advanceBookingMinInMinutes > 0) {
        const bufferedNow = new Date(now.getTime() + advanceBookingMinInMinutes * 60 * 1000)
        if (bufferedNow.toDateString() !== now.toDateString()) {
          // The buffer pushes past midnight -- getHours()/getMinutes() alone would silently
          // drop the date rollover (e.g. 23:45 + 60min = 00:45 tomorrow reads back as a
          // same-looking "0.75" fraction), making a today slot at 23:45 wrongly compare as
          // "before" that cutoff and stay offered even though nothing later today can ever
          // clear a buffer that already extends into tomorrow. 24 excludes every remaining
          // fraction of today's grid (all < 24).
          safeCutoffFraction = 24
        } else {
          const rawCutoff = getTimeFraction(bufferedNow.getHours(), bufferedNow.getMinutes())
          // Round UP to the next 15-minute grid step -- the margin of safety against
          // fetch-to-submit delay.
          safeCutoffFraction = Math.ceil(rawCutoff / 0.25) * 0.25
        }
      }

      const isInTimeSlots = (dateInterval) => {
        const dt = ensureDateObject(dateInterval)
        const timeFraction = getTimeFraction(dt.getHours(), dt.getMinutes())

        if (safeCutoffFraction !== null && timeFraction < safeCutoffFraction)
          return false

        // Look for a timeSlot for the given day the time falls within. timeSlot.start/end
        // come from the backend as raw doubles (e.g. advanceBookingAfterStoreOpenInMinutes
        // shifting an open hour by a fractional offset can leave start as 8.500000001
        // instead of 8.5) -- round them the same way timeFraction already is, or a strict
        // >= silently drops the grid point that should have matched exactly (e.g. 8:30
        // skipped in favor of 8:45/9:00).
        for (const timeSlot of timeSlots)
          if (timeFraction >= Math.round(timeSlot.start * 100) / 100 && timeFraction < Math.round(timeSlot.end * 100) / 100)
            return true

        // If not found, the time is not available
        return false
      }

      const createLabel = (str, dateInterval) => {
        if (getters.isDelivery) {
          const dt = new Date(dateInterval)
          const hours = dt.getHours()
          const minutes = dt.getMinutes()
          const deliveryRangeInMinutes = 30

          // Delivery is never just a single fixed time, but instead could fall within a fixed range
          return `${formatDateTime(hours, minutes)} - ${formatDateTime(hours, minutes+deliveryRangeInMinutes)}`
        }

        return str
      }

      let times = []

      for (let i = 0; i < 96; i++) {
        const { dateInterval, formatted } = buildInterval(i)

        // Only push intervals that pass all filters
        if (isInTimeSlots(dateInterval))
          times.push({ id: dateInterval, label: createLabel(formatted, dateInterval) })
      }

      return times
    },
    getStoreAvailableOrderDays(state, getters) {
      const { day_slots=[], asapAllowed } = getters.getValidTimeSlots
      let daysAvailable = []

      const dayMap = {
        0: `${Vue.prototype.$vuetify.t('$vuetify.hours.sun')}`,
        1: `${Vue.prototype.$vuetify.t('$vuetify.hours.mon')}`,
        2: `${Vue.prototype.$vuetify.t('$vuetify.hours.tues')}`,
        3: `${Vue.prototype.$vuetify.t('$vuetify.hours.wed')}`,
        4: `${Vue.prototype.$vuetify.t('$vuetify.hours.thurs')}`,
        5: `${Vue.prototype.$vuetify.t('$vuetify.hours.fri')}`,
        6: `${Vue.prototype.$vuetify.t('$vuetify.hours.sat')}`
      }

      const daySlotsStoreTz = getters.getStore.timeZone || getters.getStore.timezone || "America/Toronto";
      for (const day of day_slots) {
        // Need to recreate this object every index due to annoying pointer / reference issues
        // We want to start from the beginning of each day starting from today — in the
        // STORE's timezone, not the browser's. dateOffset is relative to the store's
        // "today"; anchoring off the customer's local date shifts every computed day
        // whenever the two timezones disagree on what day it currently is.
        const dt = nowInStoreTz(daySlotsStoreTz)
        dt.setHours(0)
        dt.setMinutes(0)
        dt.setSeconds(0)

        const dayOfWeek = dt.getDate() + day.dateOffset
        dt.setDate(dayOfWeek)

        // Find the available time slots for the given day
        let availableTimes = getters.getStoreAvailableOrderTimes(dt, day.time_slots)

        // Only add days that were found to have at least one available time interval
        if (availableTimes && availableTimes.length) {
          daysAvailable.push({
            id: dt,
            dow: dayOfWeek,
            label: `${dayMap[dt.getDay()]}, ${formatDate(dt, getDisplayLocale(Vue.prototype.$vuetify && Vue.prototype.$vuetify.lang && Vue.prototype.$vuetify.lang.current))}`,
            times: availableTimes
          })
        }
      }

      let menuInSchedule = true;
      if (
        getters.getMenu &&
        getters.getMenu.schedule &&
        getters.getMenu.schedule.items
      ) {
        const storeTimeZone = getters.getStore.timeZone || "America/Toronto";
        const now = new Date(
          new Date().toLocaleString("en-US", { timeZone: storeTimeZone })
        );
        menuInSchedule = isInSchedule(getters.getMenu.schedule.items, now, 0);
      }

      if (asapAllowed && menuInSchedule) {
        const scheduleTakeoutDatetimeManually = get(getters.getStore, 'settings.orderSettings.scheduleTakeoutDatetimeManually', false);
        const orderType = getters.getOrderType;

        if (scheduleTakeoutDatetimeManually && orderType !== 'DINE_IN') {
        } else {
          const estimatedPickupTimeInMinutes = get(getters.getStore, 'settings.orderSettings.estimatedPickupTimeInMinutes', 0);
          const storeTimeZone = getters.getStore.timeZone || "America/Toronto";
          const now = new Date(new Date().toLocaleString("en-US", { timeZone: storeTimeZone }));
          const soonestPickup = new Date(now.getTime() + estimatedPickupTimeInMinutes * 60000);

          // Check if ASAP pickup time falls within today's available slots
          let canShowAsapToday = true;
          if (daysAvailable.length && estimatedPickupTimeInMinutes > 0) {
            const firstDay = daysAvailable[0];
            const firstDayDate = new Date(firstDay.id);
            // Only check if firstDayDate is valid and is today
            if (!isNaN(firstDayDate.getTime())) {
              const isToday = firstDayDate.toDateString() === now.toDateString();
              if (isToday && firstDay.times && firstDay.times.length) {
                // Find the last available time slot for today (excluding any existing 'now' entries)
                const scheduledSlots = firstDay.times.filter(t => t && typeof t.id === 'string' && t.id !== 'now');
                if (scheduledSlots.length) {
                  const lastSlot = scheduledSlots[scheduledSlots.length - 1];
                  const parts = lastSlot.id.split(":");
                  if (parts.length >= 1) {
                    const h = Number(parts[0]);
                    const m = parts.length > 1 ? Number(parts[1]) : 0;
                    if (!isNaN(h) && h >= 0 && h <= 23) {
                      const lastSlotTime = new Date(firstDayDate);
                      lastSlotTime.setHours(h, m, 0, 0);
                      // If soonest pickup is after the last slot, don't show ASAP for today
                      if (soonestPickup > lastSlotTime) {
                        canShowAsapToday = false;
                      }
                    }
                  }
                }
              }
            }
          }

          if (canShowAsapToday) {
            if (!daysAvailable.length) {
              const dt = new Date()

              daysAvailable.push({
                id: dt.toString(),
                dow: dt.getDate(),
                label: `${dayMap[dt.getDay()]}, ${formatDate(dt, getDisplayLocale(Vue.prototype.$vuetify && Vue.prototype.$vuetify.lang && Vue.prototype.$vuetify.lang.current))}`,
                times: [{ id: 'now', label: Vue.prototype.$vuetify && Vue.prototype.$vuetify.t ? Vue.prototype.$vuetify.t('$vuetify.groupOrder.asap') : 'ASAP' }]
              })
            } else {
              daysAvailable[0].times = [
                { id: 'now', label: Vue.prototype.$vuetify && Vue.prototype.$vuetify.t ? Vue.prototype.$vuetify.t('$vuetify.groupOrder.asap') : 'ASAP' },
                ...daysAvailable[0].times
              ]
            }
          }
        }
      }

      return daysAvailable
    },

    getDefaultSchedule(state, getters) {
      const availableDays = getters.getStoreAvailableOrderDays;
      const scheduleTakeoutDatetimeManually = get(getters.getStore, 'settings.orderSettings.scheduleTakeoutDatetimeManually', false);
      const orderType = getters.getOrderType;
      
      if (!availableDays || !availableDays.length) {
        return { day: null, time: null };
      }
      
      const firstDay = availableDays[0];
      const firstDayTimes = firstDay.times || [];
      
      if (!firstDayTimes.length) {
        return { day: firstDay.id, time: null };
      }
      
      if (scheduleTakeoutDatetimeManually && orderType !== 'DINE_IN') {
        const nonAsapTime = firstDayTimes.find(time => time.id !== 'now');
        return {
          day: firstDay.id,
          time: nonAsapTime ? nonAsapTime.id : firstDayTimes[0].id
        };
      }

      // DEV-10499: the ORIGINAL bug here was defaulting to a real scheduled slot while
      // the chip/checkout still read as "now" -- silently placing an order for tomorrow
      // that the customer believed was ASAP. That's a display-honesty problem, not a
      // reason to leave the schedule unset entirely: formattedScheduledDateTime already
      // renders a non-'now' scheduledTime as its real date/time (e.g. "Aug 27, 9:00 AM"),
      // never as "now", so auto-filling the earliest real slot here is honest and saves
      // the customer a click on a closed/past-cutoff store, same as production_release.
      const firstSlot = firstDayTimes[0];
      return {
        day: firstDay.id,
        time: firstSlot.id
      };
    },

    scheduledDay(state, getters) {
      return getters.getDefaultSchedule.day;
    },

    scheduledTime(state, getters) {
      return getters.getDefaultSchedule.time;
    },
    getValidTimeSlots(state, getters, rootState) {
      // TODO: Would be ideal if we used the route name to grab the most up to date api
      // But grabbing route info in vuex is being annoying
      return get(getters.getServerCart, 'validTimeSlots') || {}
    },

    getHoursLimit(state, getters) {
      if (!!getters.getMenuScheduleLimit)
        return getters.getMenuScheduleLimit

      if (getters.isPickUp) {
        return get(getters, 'getStore.settings.orderSettings.scheduledOrderTimeLimitInHours', 0)
      } else if (getters.isDelivery){
        return get(getters, 'getStore.settings.orderSettings.scheduledDeliveryTimeLimitInHours', 0)
      } else {
        return 0
      }
    },
    getStoreSchedule(state, getters) {
      const orderType = getters.getOrderType || ''
      return get(getters.getStore, `settings[${orderType.toLowerCase()}Settings.schedule.items]`, [])
    },

    isDineInSingleOrder(state, getters) {
      return getters.doesStoreHaveSetting('dineInSingleOrder', false)
    },
    skipCheckout(state, getters) {
      const skip = get(state, 'store.settings.owaSettings.skipCheckout', false)
      const allOpts = get(state, 'store.settings.orderSettings.paymentSettings.ALL', [])
      const dineInOpts = get(state, 'store.settings.orderSettings.paymentSettings.DINE_IN', [])
      const opts = [ ...allOpts, ...dineInOpts ]
      const isOffline = skip === true && opts.includes('OFFLINE') // && opts.length === 1

      return isOffline && !getters.inFastServiceDineIn
    },
    groupOrderEnabled(state) {
      return get(state, 'store.settings.owaSettings.disableGroupOrder', null) !== true
    },
    showUtensilsOption(state, getters) {
      return getters.doesStoreHaveSetting('cutleryOption', false)
    },
    getGroup(state) {
      return state.group
    },
    getDineInRadiusKM(state) {
      return get(state.store, 'settings.owaSettings.dineInLocationRestrictionInKM', 0)
    },
    getStoreCountryCode(state) {
      const countryCode = get(state.store, 'country')
      const countryCodes = get(state.store, 'settings.owaSettings.addressCountryCodes')

      const validCode = (code) => {
        return isString(code) && code.length === 2 ? countryCode : null
      }

      if (!countryCodes || !countryCodes.length) {
        return validCode(countryCode)
      } else if (countryCodes.includes('ALL')) {
        return null
      } else {
        return countryCodes.filter(validCode)
      }
    },
    getGroupStores(state) {
      // Application depends on the groupId. If its not provided, we cannot display the store
      return state.groupStores
    },
    doesStoreSupportWeChatPay(state, getters) {
      const allOpts = get(state, 'store.settings.orderSettings.paymentSettings.ALL', [])
      const dineInOpts = get(state, 'store.settings.orderSettings.paymentSettings.DINE_IN', [])
      const pickUpOpts = get(state, 'store.settings.orderSettings.paymentSettings.PICKUP', [])
      const deliveryOpts = get(state, 'store.settings.orderSettings.paymentSettings.DELIVERY', [])

      const orderType = getters.getOrderType

      let opts = []
      if (orderType === 'DINE_IN') {
        opts = [ ...allOpts, ...dineInOpts ]
      } else if (orderType === 'PICKUP') {
        opts = [ ...allOpts, ...pickUpOpts ]
      } else if (orderType === 'DELIVERY') {
        opts = [ ...allOpts, ...deliveryOpts ]
      } else {
        opts = [ ...allOpts ]
      }

      return opts.includes('WECHAT')
    },

    deliveryMinMsg(state, getters) {
      const callCurrency = Vue.prototype.$currency || ((val) => val)
      const currencyCode = getters.getCurrencyCode;
      const minAmount = callCurrency(getters.getDeliveryMin, false, false, currencyCode)

      return Vue.prototype.$vuetify.t('$vuetify.cart.tooSmallForDelivery', [minAmount])
    },
    pickupMinMsg(state, getters) {
      const callCurrency = Vue.prototype.$currency || ((val) => val)
      const currencyCode = getters.getCurrencyCode;
      const minAmount = callCurrency(getters.getPickupMin, false, false, currencyCode)

      return Vue.prototype.$vuetify.t('$vuetify.cart.tooSmallForPickup', [minAmount])
    },
    punchCardError(state, getters) {
      // Display any punch card error messages returned from the server even when not blocked
      if (getters.getServerPunchCards && getters.getServerPunchCards.length) {
        return getters.getServerPunchCards
                .map(punchCard => punchCard.error)
                .filter(punchCard => !!punchCard)
                .reduce((a, b) => a + (b || ''), '')
                || null
      }

      return null
    },
    hasCouponAndIsValid(state, getters) {
      return !getters.hasAnyCouponAppliedToCart || getters.hasValidCouponAppliedToCart
    },
    discountNotUsed(state, getters) {
      // If there is an item in the cart that applies a discount, there must be another item in the cart to use said discount
      const hasDiscount = get(getters.getServerCart, 'subOrderList[0].redemptionItems[0].discountAmount', null) !== null ||
                          !!get(getters.getServerCart, 'subOrderList[0].coupons[0]', false)

      return hasDiscount && !getters.hasOrder()
    },
    cartErrorMessage(state, getters, rootState, rootGetters) {
      const isInGroupOrder = rootGetters['groupOrder/isInGroupOrder']
      // Group orders: an empty combined cart is a distinct, more common case than any of the
      // checks below (which all assume a single host cart) -- surface it explicitly instead of
      // falling through to null, which left the "blocked" checkout silently unexplained.
      if (isInGroupOrder && !rootGetters['groupOrder/groupSubTotal']) {
        return Vue.prototype.$vuetify.t('$vuetify.groupOrder.groupCartEmpty')
      }

      if (isNullOrEmpty(getters.getStoreOrderTypes))
        return 'Store order types are all disabled'

      if (!getters.storeIsAvailable) {
        // Shared-table sessions don't need a fresh "can I order right now"
        // check for the getStoreAvailableOrderDays branch specifically — the
        // mutations/fire endpoints don't always return live time slots
        // (they're not the source of truth for store hours), so that check
        // alone would false-positive mid-session. The actual "store is
        // closed" reason must still surface — a shared table doesn't stay
        // orderable if the store genuinely closes while it's in progress.
        if (rootGetters['sharedCart/isActive'] && getters.hoursAreAllowed) {
          return null
        }
        return storeBlockedReason(getters)
      }

      // If we're in the advance booking window, ASAP is not valid — require a scheduled future time
      const advanceMinutes = get(getters.getStore, 'settings.orderSettings.advanceBookingAfterStoreOpenInMinutes', 0)
      if (advanceMinutes > 0) {
        const now = new Date()
        const { open } = getStoreOpenAndClose(getters.getStore, now)
        if (open !== undefined) {
          const { hours: openHours, minutes: openMinutes } = convertTimeFraction(open)
          const openTotalMinutes = openHours * 60 + openMinutes
          const windowStartMinutes = openTotalMinutes - advanceMinutes
          const nowTotalMinutes = now.getHours() * 60 + now.getMinutes()
          const scheduledTime = get(getters.getCheckoutInfo, 'scheduledTime', '')
          const isAsap = !scheduledTime || scheduledTime === 'now'
          if (nowTotalMinutes >= windowStartMinutes && nowTotalMinutes < openTotalMinutes && isAsap) {
            return Vue.prototype.$vuetify.t('$vuetify.cart.advanceBookingWindow')
          }
        }
      }

      // DEV-10821: hasCouponAndIsValid/doesLocalCartHaveErrors read the HOST'S OWN personal
      // cart (state.cart.serverCart) -- state a group order deliberately never populates
      // (_fetchShoppingCartImpl no-ops fetchShoppingCart entirely while isInGroupOrder is
      // true). That personal cart can still hold stale leftovers from before the group
      // order started (e.g. an old coupon code), and a login-time race (fetchShoppingCart
      // firing for real, against a still-momentarily-false isInGroupOrder, before
      // recoverGroupOrder's fetch resolves) can even populate it fresh mid-session. Skip
      // both while in a group order -- unlike doesCartMeetOrderMinimum below, neither has
      // a group-aware branch to fall back on.
      if (!isInGroupOrder) {
        if (!getters.hasCouponAndIsValid)
          return getters.getCouponErrors

        if (getters.doesLocalCartHaveErrors)
          return Vue.prototype.$vuetify.t('$vuetify.cart.cartErrors')
      }

      if (getters.orderTypeIsDisabled) {
        const date = formateDateTime(ensureDateObject(getters.orderTypeIsDisabledUntil))

        if (getters.isDelivery)
          return Vue.prototype.$vuetify.t('$vuetify.cart.deliveryDisabledUntil', [ date ])
        else if (getters.isPickUp)
          return Vue.prototype.$vuetify.t('$vuetify.cart.pickupDisabledUntil', [ date ])
      }

      if (!getters.currentOrderTypeAllowed || !getters.daysAreAvailable) {
        if (getters.isDelivery)
          return Vue.prototype.$vuetify.t('$vuetify.cart.deliveryDisabled')

        if (getters.isPickUp)
          return Vue.prototype.$vuetify.t('$vuetify.cart.pickupDisabled')
      }

      if (!getters.daysAreAvailable) {
        if (getters.isDelivery)
          return Vue.prototype.$vuetify.t('$vuetify.cart.deliveryUnavailable')
        else if (getters.isPickUp)
          return Vue.prototype.$vuetify.t('$vuetify.cart.pickupUnavailable')
      }

      if (!getters.doesCartMeetOrderMinimum) {
        if (getters.isDelivery)
          return getters.deliveryMinMsg
        else if (getters.isPickUp)
          return getters.pickupMinMsg
      }

      // Same reasoning as hasCouponAndIsValid/doesLocalCartHaveErrors above -- all three
      // read the host's personal cart/punch cards, not the group's.
      if (!isInGroupOrder) {
        if (getters.punchCardError)
          return getters.punchCardError

        if (getters.discountNotUsed)
          return Vue.prototype.$vuetify.t('$vuetify.cart.discountNotUsed')

        if (!!getters.cartValidationErrors)
          return getters.cartValidationErrors
      } else if (!!rootGetters['groupOrder/cartValidationErrors']) {
        // Unlike punch cards/coupons above, item-level validation errors (out of
        // stock, etc.) DO apply to the group flow -- the participant cart carries
        // validationErrors the same way the normal cart's server cart does.
        return rootGetters['groupOrder/cartValidationErrors']
      } else if (rootGetters['groupOrder/anyParticipantCartHasErrors']) {
        // My own cart is clean, but another participant's isn't -- blockCheckout also
        // blocks on this (see anyParticipantCartHasErrors), so the host needs a reason
        // here too, not just a silently-disabled button. The specific item/guest is
        // already visible in the "Others in your group" panel (Cart.vue).
        return Vue.prototype.$vuetify.t('$vuetify.groupOrder.memberCartHasErrors')
      }

      return null
    },
    blockCheckout(state, getters, rootState, rootGetters) {
      // Group orders: an empty host cart is normal (other members may hold all
      // the items), so check the group's combined item count instead of the
      // host's own sub-order.
      const isInGroupOrder = rootGetters['groupOrder/isInGroupOrder']
      const noOrder = isInGroupOrder
        ? !rootGetters['groupOrder/groupSubTotal']
        : !(getters.hasOrder() || !!getters.getExistingOrder.length)

      // cartErrorMessage only covers the HOST'S OWN cart (groupOrder/cartValidationErrors)
      // -- a guest's out-of-stock item is a separate, additional block the host must also
      // respect, not something cartErrorMessage's banner text already surfaces.
      const anyGuestCartHasErrors = isInGroupOrder && rootGetters['groupOrder/anyParticipantCartHasErrors']

      return !!getters.cartErrorMessage || anyGuestCartHasErrors || noOrder || getters.orderTypeIsDisabled
    },

    allowAnonymousDineIn(state, getters, rootState) {
      const allowAnon = get(getters.getStore, 'settings.owaSettings.allowAnonymousDineInOrder', false)
      const requireKiosk = get(getters.getStore, 'settings.owaSettings.allowAnonymousDineInKioskMode', false)

      return allowAnon && (!requireKiosk || getters.isInKioskMode)
    }
  },
  actions: {
    setCustomHeader({ commit }, val) {
      commit("SET_CUSTOM_HEADER", !!val);
    },
    setDineInOnly({ commit, getters }, dineInOnly) {
      commit("SET_DINE_IN_ONLY", dineInOnly);
    },
    setIsGroupStoresLoading({ commit }, val) {
      commit('SET_IS_GROUP_STORES_LOADING', !!val)
    },
    setGroupStoresSort({ commit }, sort) {
      commit('SET_GROUP_STORES_SORT', sort)
    },
    batchFetchStoresForGroup({ getters, commit, dispatch }) {
      dispatch('setIsGroupStoresLoading', true);
      const { storeIds = [], groupId } = getters.getGroup || getters.getStore.groupId
        const headers = {
            storeId : "NULL",
            'white-label-group-id': groupId
        }
      // groupId is not a valid API parameter but we need this to bust Google's cache
      // when they are indexing the site
      const request = this.$api.get(`/store?groupId=${groupId}`, { headers })

      return request.then((result => {
          const stores = result.data;
          commit('SET_GROUP_STORES', stores)
        }))
        .finally(() => {
          dispatch('setIsGroupStoresLoading', false)
        })
    },
    async fetchStore({ commit, dispatch, getters }, { gid, sid, mid }) {
      if (!sid) {
        commit('SET_STORE_LOADING', false)
        return;
      }

      commit('SET_STORE_LOADING', true)

      const headers = {
        storeId: sid,
        'white-label-group-id': gid
      }
      const oldId = getters.getStore.storeId
      let catalogHolidayHours = []
      if (
        getters.getStore.catalogHolidayHours &&
        getters.getStore.catalogHolidayHours.length > 0
      ) {
        catalogHolidayHours = getters.getStore.catalogHolidayHours;
      }

      // storeId and groupId are not valid API parameters
      // but we need this to bust Google's cache when they are indexing the site
      const store = this.$api.get(`/store?storeId=${sid}&groupId=${gid}`, { headers })
        .then(async res => {
          let store = res.data[ 0 ] || {}
          if (store.storeId == getters.getStore.storeId && catalogHolidayHours.length > 0) {
            store.catalogHolidayHours = catalogHolidayHours;
          }
          commit('SET_STORE', store)
          commit('SET_STORE_ID', store.storeId)
          dispatch('setOrderTypeIfNotSet')
          determineInitialLang(getters, dispatch)

          if (mid)
            await dispatch('fetchAllMenus', mid)

          dispatch('setDefaultCheckoutInfoIfEmpty', null, { root: true })
          return store
        })
        .catch(error => {
          if (error.response && error.response.data && error.response.data.errorCode === 'GENERAL_ERR_STORE_NOT_FOUND') {
            console.error('Store not found in fetchStore:', sid);
            throw error;
          }
          throw error;
        })
        .finally(() => {
          commit('SET_STORE_LOADING', false)
        })

      await store
    },
    async setStoreId({ commit, dispatch, getters }, { gid, sid, mid }) {
        return commit("SET_STORE_ID", sid);
    },

    fetchDynamicManifest({ commit, dispatch, state }, gid) {
      document.querySelector('#snappy-manifest').setAttribute('href', process.env.VUE_APP_API_MANIFEST + '/manifest/' + gid)
      dispatch('setLoading', true)

      return this.$api.get(process.env.VUE_APP_API_MANIFEST + '/manifest/' + gid)
        .then(res => {
          const manifest = res.data

          Vue.prototype.$vuetify.theme = manifest.meta
            ? Object.assign(Vue.prototype.$vuetify.theme, {
              ...manifest.meta,
              menu_colour: manifest.background_color || 'white'
            })
            : Vue.prototype.$vuetify.defaultTheme
        })
        .catch(err => {
          Vue.prototype.$vuetify.theme = Vue.prototype.$vuetify.defaultTheme
        })
        .finally(() => {
          dispatch('setLoading', false)

          // const isIos = () => {
          //   const userAgent = window.navigator.userAgent.toLowerCase()
          //   return /iphone|ipad|ipod/.test(userAgent)
          // }
          // // Detects if device is in standalone mode
          // const isInStandaloneMode = () => ('standalone' in window.navigator) && (window.navigator.standalone)
          //
          // // Checks if should display install popup notification:
          // if (isIos() && !isInStandaloneMode() && !isWeChat()) {
          //   const cssClass = Vue.config.environment === 'production' ? 'share-prod text-xs-left' : 'share text-xs-left'
          //   dispatch('showSnack', {
          //     text: `<p>${Vue.prototype.$vuetify.t('$vuetify.iosBanner.partOne')} <span class="share text-xs-left"></span> ${Vue.prototype.$vuetify.t('$vuetify.iosBanner.partTwo')} <i class="v-icon material-icons add-to-home">add_box</i> ${Vue.prototype.$vuetify.t('$vuetify.iosBanner.partThree')}</p>`,
          //     color: 'iosInstall'
          //   })
          // }
        })
    },
    async fetchStoreDetails({ commit, dispatch, state, getters }, {sid, mid}) {
      document.querySelector('#snappy-manifest').setAttribute('href', process.env.VUE_APP_API_MANIFEST + '/store_details/' + sid)
      dispatch('setLoading', true)
      commit('SET_STORE_LOADING', true)
      return this.$api.get(process.env.VUE_APP_API_MANIFEST + '/store_details/' + sid)
        .then(async res => {
          const manifest = res.data.manifest
          const store = res.data.store[0]
          if (res.data.catalogHolidayHours && res.data.catalogHolidayHours.length > 0) {
            store.catalogHolidayHours = res.data.catalogHolidayHours;
          }
          store.posEnabled = res.data.posEnabled;
          const storeGroup = res.data.storeGroup
          dispatch('setGroupStore', { gid: store.groupId, loadingForce: true})
          commit('SET_STORE', store)
          commit('SET_STORE_ID', store.storeId)
          determineInitialLang(getters, dispatch)
          if (mid)
            await dispatch('fetchAllMenus', mid)
          dispatch('setOrderTypeIfNotSet')
          dispatch('setDefaultCheckoutInfoIfEmpty', null, { root: true })
          if (manifest) {
            Vue.prototype.$vuetify.theme = manifest.meta
              ? Object.assign(Vue.prototype.$vuetify.theme, {
                ...manifest.meta,
                menu_colour: manifest.background_color || 'white'
              })
              : Vue.prototype.$vuetify.defaultTheme
          } else {
            Vue.prototype.$vuetify.theme = Vue.prototype.$vuetify.defaultTheme
          }
          return res.data
        })
        .catch(err => {
          Vue.prototype.$vuetify.theme = Vue.prototype.$vuetify.defaultTheme
        })
        .finally(() => {
          dispatch('setLoading', false)
          commit('SET_STORE_LOADING', false)

          // const isIos = () => {
          //   const userAgent = window.navigator.userAgent.toLowerCase()
          //   return /iphone|ipad|ipod/.test(userAgent)
          // }
          // // Detects if device is in standalone mode
          // const isInStandaloneMode = () => ('standalone' in window.navigator) && (window.navigator.standalone)
          //
          // // Checks if should display install popup notification:
          // if (isIos() && !isInStandaloneMode() && !isWeChat()) {
          //   const cssClass = Vue.config.environment === 'production' ? 'share-prod text-xs-left' : 'share text-xs-left'
          //   dispatch('showSnack', {
          //     text: `<p>${Vue.prototype.$vuetify.t('$vuetify.iosBanner.partOne')} <span class="share text-xs-left"></span> ${Vue.prototype.$vuetify.t('$vuetify.iosBanner.partTwo')} <i class="v-icon material-icons add-to-home">add_box</i> ${Vue.prototype.$vuetify.t('$vuetify.iosBanner.partThree')}</p>`,
          //     color: 'iosInstall'
          //   })
          // }
        })
    },
    setGroupStore({ commit, dispatch, state }, data) {
      const { gid, loadingForce = false } = data || {}
      const headers = gid ? { 'white-label-group-id': gid } : undefined

      // groupId is not a valid API parameter but we need this to bust Google's cache
      // when they are indexing the site
      return this.$api.get(`/store_group?groupId=${gid}`, { headers }).then(res => {
        commit('SET_GROUP', res.data)
        const { stores } = res.data

        // If the number of stores differ from what we have in the state the user has most likely
        // gone to a store page directly. The old groupStores data is then invalid, and must be
        // set with the new stores from the group.
        if (stores.length !== state.groupStores.length) {
          commit('SET_GROUP_STORES', stores)
        }
      })
      .catch(err => {
      })
      .finally(() => {
        if (!loadingForce) {
          dispatch("setLoading", false);
        }
      })
    },
    async changeLang({ dispatch, getters }, code = null) {
      if (typeof code !== 'string') {
        code = null;
      }
      const storeLangs = getters.getStoreLanguages;
      const hardCodedLangs = [
        { code: "en", name: "English" },
        { code: "fr", name: "Francais" },
        { code: "zh", name: "中文" },
        { code: "zh-TW", name: "繁體中文" },
        { code: "ko", name: "한국어" },
        { code: "vi", name: "Tiếng Việt" },
        { code: "th", name: "ไทย" },
        { code: "ar", name: "العربية" }
      ];

      const updateLanguage = async (newLang) => {
        await dispatch('doChangeLang', { code: convertLangCode(newLang), isManualSelection: true });
        if (getters.getStore) {
          dispatch('fetchShoppingCart');
          if (getters.getMenu && getters.getMenu.code) {
            await dispatch('fetchAllMenus', getters.getMenu.code);
          }
          dispatch('fetchOptions');
          dispatch('fetchAllCoupons');
          if (getters.isLoggedIn) {
            dispatch('fetchAccountDetails', getters.getStore.storeId);
          }
        }
      };
      if (code) {
        await updateLanguage(code);
      }
      if (storeLangs.length > 1 && !code) {
        const curLang = revertLangCode(Vue.prototype.$vuetify.lang.current);
        const curIndex = storeLangs.findIndex(l => l.code == curLang);
        const newIndex = curIndex < storeLangs.length-1 ? curIndex+1 : 0;
        const newLang = storeLangs[newIndex] ? storeLangs[newIndex].code : 'en';
        await updateLanguage(newLang);
      }
      if (!storeLangs.length && !code) {
        const group = getters.getGroup;
        if (group && group.groupNameTranslation && group.groupNameTranslation.translations && group.groupNameTranslation.translations.length > 0) {
          const extractedLangs = group.groupNameTranslation.translations.map((item) => item.lang);
          const filteredLangs = hardCodedLangs.filter(lang => extractedLangs.includes(lang.code));
          const curLang = revertLangCode(Vue.prototype.$vuetify.lang.current);
          const curIndex = filteredLangs.findIndex((l) => l.code == curLang);
          const newIndex = curIndex < filteredLangs.length-1 ? curIndex+1 : 0;
          const newLang = filteredLangs[newIndex] ? filteredLangs[newIndex].code : 'en';
          await dispatch("doChangeLang", { code: convertLangCode(newLang), isManualSelection: true });
        }
      }
    },
    doChangeLang({ dispatch }, payload) {
      let code, isManualSelection = false;
      if (typeof payload === 'string') {
        code = payload;
      } else {
        code = payload.code;
        isManualSelection = payload.isManualSelection || false;
      }

      Vue.prototype.$vuetify.lang.current = code
      Vue.prototype.$i18nPointer.locale = code;
      if (Vue.prototype.$i18n) {
        Vue.prototype.$i18n.locale = code;
      }
      localStorage.setItem('lang', code)
      Vue.prototype.$bus.$emit('langChanged', code)
      if (isManualSelection) {
        localStorage.setItem('langManuallySelected', 'true')
      }

      if (this.$router.history.current.name == 'group')
        dispatch('batchFetchStoresForGroup')
    }
  },
  mutations: {
    SET_STORE(state, store) {
      localStorage.removeItem('vuex')
      state.store = normalizeStoreSlug(store)
    },
    SET_STORE_LOADING(state, loading) {
      state.isStoreLoading = loading

      // make sure the spinner is visible
      if (loading) {
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'instant'
        });
      }
    },
    SET_GROUP(state, group) {
      state.group = {}
      state.group = group
    },
    SET_GROUP_STORES(state, stores) {
      state.groupStores = stores.map(normalizeStoreSlug)
    },
    SET_GROUP_STORES_SORT(state, sort) {
      state.groupStoresSort = sort
    },
    SET_IS_GROUP_STORES_LOADING(state, val) {
      state.isGroupStoresLoading = !!val
    },
    SET_DINE_IN_ONLY(state, val) {
      state.dineInOnly = val
    },
    SET_CUSTOM_HEADER(state, val) {
      state.customHeader = !!val
    },
  }
}
